import { useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import axios, { API } from "@/utils/api";

const ImageUploader = ({ entryId, onUploadComplete, onLocalImageAdd }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  const processFile = async (file) => {
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPG, PNG, GIF, or WebP)");
      return;
    }

    // If no entry yet, store locally for preview
    if (!entryId) {
      const localUrl = URL.createObjectURL(file);
      onLocalImageAdd({ file, localUrl });
      toast.success("Image added! It will be uploaded when you save. ✨");
      return;
    }

    // If entry exists, upload immediately
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/entries/${entryId}/images`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Image uploaded successfully! ✨");
      onUploadComplete(response.data);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`upload-zone ${isDragging ? "dragging" : ""}`}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      data-testid="image-uploader"
    >
      <input
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        id="image-upload"
        disabled={uploading}
      />
      <label htmlFor="image-upload" className="cursor-pointer">
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <div className="loading-spinner" />
            <span style={{ color: 'var(--theme-text)' }}>Uploading...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="w-10 h-10" style={{ color: 'var(--theme-primary)' }} />
            <span style={{ color: 'var(--theme-text)' }}>
              Drop an image here or click to upload
            </span>
            <span className="text-xs" style={{ color: 'var(--theme-primary)', opacity: 0.6 }}>
              JPG, PNG, GIF, or WebP
            </span>
          </div>
        )}
      </label>
    </div>
  );
};

export default ImageUploader;
